import lowerFirst from "lodash/lowerFirst";
import forEach from "lodash/forEach";
import { Currency } from "@Types/api/price";
import CartContent, { Article, PaymentMethods } from "@Types/cart";
import {
  CheckoutStepProperties,
  ConvertedCartProductProperties,
  DataLayerProperties,
  IdentifyProperties,
  OrderUpdatedProperties,
  TrackEvent,
  TrackEventName,
  TrackEventType,
} from "@Types/track";
import User from "@Types/user";
import { getSelectedDeliveryMethod } from "@Utils/cart/deliveryMethods";
import { getMetaContent } from "@Utils/meta";

interface TrackerOptionals {
  openTime?: number;
}

interface CustomizationLeaveProperties {
  customization: { leaveReason: string; leaveDuration: number };
  product: { productTag: string };
}

export const track = (
  name: string,
  productTag: string,
  optionals: TrackerOptionals
): Promise<void> => {
  const { label, properties } = getTrackerByName(name, productTag, optionals);
  if (!window.analytics || !window.analytics.track || !label) {
    return Promise.resolve();
  }
  return Promise.race([
    window.dataLayer.push({
      event: `${label} Data`,
      track: {
        properties,
      },
    }),
    new Promise((resolve) => {
      setTimeout(resolve, 3000);
    }),
  ]);
};

export const trackKustom = (
  name: string,
  productTag: string,
  optionals: TrackerOptionals
): Promise<void> => {
  if (!window.analytics || !window.analytics.track || !name) {
    return Promise.resolve();
  }
  const nameForCheerz = getTrackerLabelForKustom(name);
  return track(nameForCheerz, productTag, optionals);
};

export const getTrackerByName = (
  name: string,
  productTag: string,
  optionals: TrackerOptionals
): { label: string; properties: CustomizationLeaveProperties } => {
  let label = "";
  const properties = {
    customization: { leaveReason: "", leaveDuration: 0 },
    product: { productTag },
  };
  switch (name) {
    case "kustom finalize clicked":
      label = "Customization Leave Clicked";
      properties.customization.leaveReason = "Add to cart";
      properties.customization.leaveDuration = getElapsed(optionals.openTime);
      break;
    case "kustom quit clicked":
      label = "Customization Leave Clicked";
      properties.customization.leaveReason = "Exit";
      properties.customization.leaveDuration = getElapsed(optionals.openTime);
      break;
  }
  return { label, properties };
};

const getTrackerLabelForKustom = (name: string): string => {
  switch (name) {
    case "finalize clicked":
      return "kustom finalize clicked";
    case "quit clicked":
      return "kustom quit clicked";
  }
  return name;
};

const getElapsed = (startTime: number | undefined): number => {
  return startTime ? Date.now() - startTime : 0;
};

export const convertToCheckoutStepProperties = (
  stepCategory: string,
  stepNumber: number,
  cart: CartContent,
  paymentMethod?: PaymentMethods
): CheckoutStepProperties => {
  const shippingMethod = getSelectedDeliveryMethod(cart);

  return {
    checkoutId: cart.orderNumber,
    paymentMethod: paymentMethod || "",
    shippingMethod: shippingMethod ? shippingMethod.name : "",
    stepCategory,
    stepNumber,
  };
};

export const convertToCartProductProperties = (
  article: Article
): ConvertedCartProductProperties => ({
  brand: "Cheerz",
  category: article.product.statProductGroupTag,
  name: article.product.name,
  price: article.total.cents / 100 / article.quantity,
  product_id: article.product.tag,
  quantity: article.quantity,
  sku: article.product.tag,
});

export const convertToOrderUpdatedProperties = (
  cart: CartContent,
  category: string
): OrderUpdatedProperties => ({
  category,
  couponId: cart.promoCode || "",
  discount: cart.promoTotal.cents,
  products: cart.articles.map(convertToCartProductProperties),
  revenue: cart.revenue,
  shipping: cart.shippingTotal.cents,
  tax: cart.tax,
  total: cart.total.cents,
});

export const convertToIdentifyProperties = (user: User | undefined): IdentifyProperties => {
  let emarsysId = user ? user.userId : "";

  if (user && getMetaContent("cheerz-env") && getMetaContent("cheerz-env") !== "production") {
    emarsysId = `${getMetaContent("cheerz-env")}_${user.userId}`;
  }

  return {
    emarsysId,
    md5Email: user ? user.md5Email : "",
    userId: user ? user.userId : "",
  };
};

const pushDataLayerEvent = (event: TrackEvent, properties?: DataLayerProperties): void => {
  if (typeof window.dataLayer !== "undefined") {
    properties
      ? event.category
        ? window.dataLayer.push({
            event: `${event.name} Data`,
            [event.type]: { [event.category]: properties },
          })
        : window.dataLayer.push({
            event: `${event.name} Data`,
            [event.type]: properties,
          })
      : window.dataLayer.push({
          event: `${event.name} Data`,
        });
  }
};

const resetCartProducts = (): void => {
  if (typeof window.dataLayer !== "undefined") {
    window.dataLayer.push({
      track: {
        cart: { products: undefined },
      },
    });
  }
};

export const setDataLayerMandatoryPageTrackers = (
  currency: Currency | undefined,
  lang: string | undefined,
  namePage: string,
  categoryPage: string,
  path: string,
  user: User
) => {
  setDataLayerTrack("Global", {
    currency,
    environment: getMetaContent("cheerz-env") === "production" ? "production" : "test",
    locale: lang,
  });
  setDataLayerTrack("Page", {
    category: categoryPage,
    name: namePage,
    path,
    version: "A",
  });
  setDataLayerTrack("Identify", convertToIdentifyProperties(user));
};

export const setDataLayerTrack = (
  eventName: TrackEventName,
  properties?: DataLayerProperties
): void => {
  forEach(properties, (value: number, key) => {
    if (key === "articlePrice" || "discount" || "revenue" || "shipping" || "tax" || "total") {
      value = value / 100;
    }
  });

  const trackCartTypeEvent: TrackEvent = { category: "cart", name: eventName, type: "track" };
  const trackCategoryTypeEvent: TrackEvent = {
    category: "category",
    name: eventName,
    type: "track",
  };
  const trackNewsletterTypeEvent: TrackEvent = {
    category: "newsletter",
    name: eventName,
    type: "track",
  };
  const trackProductTypeEvent: TrackEvent = { category: "product", name: eventName, type: "track" };
  const trackPromotionTypeEvent: TrackEvent = {
    category: "promotion",
    name: eventName,
    type: "track",
  };

  switch (eventName) {
    case "Global":
    case "Identify":
    case "Page":
      pushDataLayerEvent(
        { name: eventName, type: lowerFirst(eventName) as TrackEventType },
        properties
      );
      break;
    case "Cart Viewed":
    case "Checkout Started":
    case "Order Updated":
      resetCartProducts();
      pushDataLayerEvent(trackCartTypeEvent, properties);
      break;
    case "Newsletter Login Checked Data":
      pushDataLayerEvent(trackNewsletterTypeEvent, properties);
      break;
    case "Product List Clicked":
      pushDataLayerEvent(trackCategoryTypeEvent, properties);
      break;
    case "Promotion Clicked":
    case "Promotion Viewed":
      pushDataLayerEvent(trackPromotionTypeEvent, properties);
      break;
    case "Address Error Occured":
    case "Cart Article Edit Clicked":
    case "Cart Promo Codes List Clicked":
    case "Cart Shipping Method Clicked":
    case "Cart Shipping Methods Viewed":
    case "Checkout Step Completed":
    case "Checkout Step Viewed":
    case "Coupon Applied":
    case "Coupon Denied":
    case "Coupon Removed":
    case "Payment Change Address Clicked":
    case "Payment Error Occured":
    case "Payment Street Number Alert Viewed":
    case "Payment Without Accepting Terms Clicked":
    case "Product Removed":
    case "Wrong Zip Code Modal Correct Clicked":
    case "Wrong Zip Code Modal Ignore Clicked":
      pushDataLayerEvent(trackCartTypeEvent, properties);
      break;
    case "Saved Products Continue Clicked Data":
    case "Saved Products Remove Clicked Data":
      pushDataLayerEvent(trackProductTypeEvent, properties);
      break;
    default:
      throw new Error("Unsupported dataLayer event");
  }
};
